import i18n from 'i18next';
import {type ButtonProps, DataStatus, Dialog, IconButton} from 'platform/components';
import {Heading, Show, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {suffixTestId, type TestIdProps} from 'shared/web';

import type {EntityResourceResolver} from '../../context/EntityResourceResolver';
import {useBulkOperationRecovery} from '../../hooks/useBulkOperationRecovery';
import {useBulkOperationTracking} from '../../hooks/useBulkOperationTracking';
import {useIsMobileView} from '../../hooks/useIsMobileView';
import {type BulkOperationResponseBody} from '../../types/api/bulkOperations';
import {type BulkOperationActionDefinitions} from '../../types/BulkOperationActionDefinition';
import type {BulkOperationsServices} from '../../types/BulkOperationsServices';
import {getBulkOperationActionDefinition} from '../../utils/getBulkOperationActionDefinition';
import {getBulkOperationRecoveryAction} from '../../utils/getBulkOperationRecoveryAction';
import {isBulkOperationTerminal} from '../../utils/isBulkOperationTerminal';
import {BulkOperationResult} from '../BulkOperationResult/BulkOperationResult';
import {BulkOperationSubtitle} from '../BulkOperationSubtitle/BulkOperationSubtitle';

type BulkOperationDialogProps = TestIdProps & {
  services: BulkOperationsServices;
  entityResourceResolver?: EntityResourceResolver;
  actionsDefinition: BulkOperationActionDefinitions;
  bulkOperationId: string;
  operation?: BulkOperationResponseBody;
  tracking?: ReturnType<typeof useBulkOperationTracking>['linked'];
  recovery?: ReturnType<typeof useBulkOperationRecovery>;
  onClose: () => void;
  onOperationChange: (operation: BulkOperationResponseBody) => void;
  onTryAgain: (actionCode: string) => void;
};

export function BulkOperationDialog(props: BulkOperationDialogProps) {
  if (props.recovery === undefined) {
    return <OwnedBulkOperationDialog key={props.bulkOperationId} {...props} />;
  }
  return props.tracking === undefined ? (
    <TrackedBulkOperationDialog key={props.bulkOperationId} {...props} recovery={props.recovery} />
  ) : (
    <BulkOperationDialogContent
      key={props.bulkOperationId}
      {...props}
      tracking={props.tracking}
      recovery={props.recovery}
    />
  );
}

function OwnedBulkOperationDialog(props: BulkOperationDialogProps) {
  const recovery = useBulkOperationRecovery({
    services: props.services,
    onRegistered: props.onOperationChange,
  });
  return <BulkOperationDialog {...props} recovery={recovery} />;
}

function TrackedBulkOperationDialog(
  props: BulkOperationDialogProps & {recovery: ReturnType<typeof useBulkOperationRecovery>}
) {
  const tracking = useBulkOperationTracking({
    services: props.services,
    linkedOperationId: props.bulkOperationId,
    initialOperation: props.operation,
  });
  return <BulkOperationDialogContent {...props} tracking={tracking.linked} />;
}

function BulkOperationDialogContent(
  props: BulkOperationDialogProps & {
    tracking: ReturnType<typeof useBulkOperationTracking>['linked'];
    recovery: ReturnType<typeof useBulkOperationRecovery>;
  }
) {
  const isMobileView = useIsMobileView();
  const {operation: data, isLoading, isError} = props.tracking;
  const isRerunPending = data !== undefined && props.recovery.pendingIds.has(data.bulkOperationId);

  const actionDefinition =
    data !== undefined
      ? getBulkOperationActionDefinition(data.actionCode, props.actionsDefinition)
      : {title: i18n.t('general.actions.bulkActions')};
  const outcomeLabel =
    data?.state === 'FAILED' || data?.state === 'RESOLVE_FAILED'
      ? i18n.t('general.notifications.labels.failed')
      : i18n.t('entity.import.labels.finished');
  const title =
    data !== undefined && isBulkOperationTerminal(data.state)
      ? `${actionDefinition.title} ${outcomeLabel.toLocaleLowerCase(i18n.resolvedLanguage)}`
      : actionDefinition.title;

  const recoveryAction = data !== undefined ? getBulkOperationRecoveryAction(data.state) : null;
  const recoveryButtons = match(recoveryAction)
    .with('rerunFailed', () => [
      {
        title: i18n.t('general.actions.rerunFailed', {defaultValue: 'Rerun failed'}),
        suffix: `(${data?.failedCount ?? 0})`,
        variant: 'outlined' as const,
        onClick: () => {
          if (data !== undefined) {
            void props.recovery.rerun(data);
          }
        },
        isLoading: isRerunPending,
        isDisabled: isRerunPending,
      },
    ])
    .with('tryAgain', () => [
      {
        title: i18n.t('general.actions.tryAgain'),
        variant: 'outlined' as const,
        onClick: () => {
          if (data !== undefined) {
            props.onTryAgain(data.actionCode);
          }
        },
      },
    ])
    .with(null, () => [])
    .exhaustive();
  const buttons: ButtonProps[] = [
    ...recoveryButtons,
    {
      title: i18n.t('page.datagrid.labels.done'),
      variant: 'primary',
      onClick: props.onClose,
    },
  ];

  return (
    <Dialog
      isOpen
      onClose={props.onClose}
      disableBodyPadding
      initialFocusTestId="bulkOperationDetailClose"
      size={isMobileView ? 'xLarge' : 'default'}
      buttons={buttons}
      data-testid={suffixTestId('bulkOperationStatus', props)}
    >
      <div className="flex max-h-[calc(100dvh-12rem)] flex-col border-b! border-solid! border-separator">
        <header className="flex shrink-0 items-start justify-between gap-4 p-4 sm:p-6">
          <VStack spacing={1}>
            <Heading size={3} headingLevel={2}>
              {title}
            </Heading>
            <Show when={data}>
              {(operation) => (
                <BulkOperationSubtitle
                  operation={operation}
                  presentation={props.services.presentation}
                />
              )}
            </Show>
          </VStack>
          <IconButton
            icon="navigation/close"
            aria-label={i18n.t('general.actions.close')}
            onClick={props.onClose}
            data-testid={suffixTestId('bulkOperationDetailClose', props)}
          />
        </header>
        <div className="flex min-h-0 flex-col px-4 pb-4 sm:px-6 sm:pb-6">
          <DataStatus isLoading={isLoading} isError={isError} minHeight={24}>
            <Show when={data}>
              <BulkOperationResult
                services={props.services}
                entityResourceResolver={props.entityResourceResolver}
                actionDefinition={actionDefinition}
                operation={data}
                testId={props['data-testid']}
              />
            </Show>
          </DataStatus>
        </div>
      </div>
    </Dialog>
  );
}
